import React, { useState, useEffect, useContext, useRef } from 'react';
import { Box, Button, Radio, Divider, Typography, Alert } from '@mui/material';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import { AppContext, SnackContext, ThemeContext } from 'src/store/ContextStore';
import FormLabel from 'src/components/UI/input/CustomFormLabel';
import GailForm from './GailForm';
import IndividualDataForm, {
  INDIVIDUAL_DATA_FIELDS
} from './IndividualDataForm';
import PrsForm from './PrsForm';
import ClinicalMeasurementsForm from './ClinicalMeasurementsForm';
import Qrisk3StoredResult from '../../qrisk3/Qrisk3StoredResult';
import PackResult, { fromStored } from '../../triage/PackResult';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import {
  ChangedSectionMarker,
  UnsavedChangeCount,
  useChangedFields
} from 'src/components/UI/input/changed-field';
import UnsavedChangesDialog from 'src/components/UI/dialog/UnsavedChangesDialog';

// Maps CanRisk's three built-in PRS fields to a keyword matched against a
// saved peer 'prs' record's free-text trait — lets a breast/ovarian/prostate
// PRS record entered via the PRS tab be suggested here instead of re-typed.
// z-score is suggested for ANY trait-matched record (it's a general
// statistical value). alpha is only ever suggested when the record's pgsId
// is a specific panel CanRisk has published a verified alpha for (see
// canriskReference, fetched from /risk-factor/prs/canrisk-reference) — never
// by trait keyword, since alpha is panel-specific, not disease-specific.
const PRS_TRAIT_KEYWORDS = {
  prsBreast: 'breast',
  prsOvarian: 'ovarian',
  prsProstate: 'prostate'
};

function buildPrsSuggestions(prsRecords, canriskReference = {}) {
  const suggestions = {};
  Object.entries(PRS_TRAIT_KEYWORDS).forEach(([canriskKey, keyword]) => {
    const matches = prsRecords
      .filter(
        (r) =>
          r.prsResult?.zscore != null &&
          (r.prsResult?.trait || '').toLowerCase().includes(keyword)
      )
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    const match = matches[0] || null;
    const verifiedPanel = match
      ? canriskReference[String(match.prsResult?.pgsId).toUpperCase()]
      : null;
    suggestions[canriskKey] = match
      ? { ...match, verifiedAlpha: verifiedPanel || null }
      : null;
  });
  return suggestions;
}

/**
 * Risk Factor clinical record — one modal that toggles between the CanRisk
 * "Individual Data" form, the Gail score form, and the PRS form (radio
 * switch).
 * - Gail: GailForm (self-saving via its Calculate button → gail record).
 * - CanRisk: IndividualDataForm, prefilled for the proband, saved to the
 *   member's canrisk record. Created records surface as "Risk Score" tiles.
 * - PRS: PrsForm (self-saving, no in-app calculation) → peer 'prs' record,
 *   tagged with the source model (pgsId/pgsName).
 */
// CanRisk's individual data, by key, in screen order (TGCLOUD-1902).
const CANRISK_KEYS = [
  ...INDIVIDUAL_DATA_FIELDS.left,
  ...INDIVIDUAL_DATA_FIELDS.right,
  ...INDIVIDUAL_DATA_FIELDS.prs
].map((field) => field.key);

export default function RiskFactorForm({
  data,
  handleClose,
  isProband,
  familyMemberId,
  isDisabled,
  // How many fields hold an unsaved change: what closing it asks by.
  onChangeCount,
  model
}) {
  const t = useAutoTranslation();

  const { content } = useContext(AppContext);
  const { setSnack } = useContext(SnackContext);
  const { theme } = useContext(ThemeContext);
  const user = content?.pedigreeSelectedUsers?.selectedUser;
  const userId = user?.user_id || user?._id;

  // Settings-style card that holds the selected form below the divider.
  const isLight = theme?.appearance?.value === 'light';
  const formCardSx = {
    bgcolor: isLight ? '#fff' : 'rgba(255,255,255,0.05)',
    border: '0.5px solid',
    borderColor: 'divider',
    borderRadius: '12px',
    p: 2.5,
    mb: 1
  };

  const gender = data?.gender;
  const gailAllowed = gender === 'female';
  // Clinical measurements (TGCLOUD-1911) are offered while QRISK3 is on, and
  // a record already made stays openable whatever the switch says.
  const measurementsOffered =
    content?.settings?.systemFeatures?.FEATURE_QRISK3 === true ||
    model?.recordType === 'measurements';

  // Canonical member key — same id everywhere (never the literal 'proband').
  const memberId = data?.proBandId ?? familyMemberId;

  // When opened from an existing card, preselect that record's model.
  const [riskModel, setRiskModel] = useState(
    model?.recordType || (gailAllowed ? 'gail' : 'canrisk')
  );
  const [individualData, setIndividualData] = useState({});
  const [savingCanrisk, setSavingCanrisk] = useState(false);
  const [prsSuggestions, setPrsSuggestions] = useState({});
  const [prsRecords, setPrsRecords] = useState([]);
  const [canriskReference, setCanriskReference] = useState({});
  const gailRef = useRef(null);
  const prsRef = useRef(null);
  const measurementsRef = useRef(null);

  // UNSAVED CHANGES (TGCLOUD-1902). Gail and PRS keep their own inputs and
  // report (unsaved, shown) counts; CanRisk's individual data lives here and is
  // compared here, against what was loaded for the member.
  const [subCounts, setSubCounts] = useState({
    gail: { unsaved: 0, shown: 0 },
    prs: { unsaved: 0, shown: 0 },
    measurements: { unsaved: 0, shown: 0 }
  });
  const reportFrom = (which) => (unsaved, shown) =>
    setSubCounts((prev) =>
      prev[which].unsaved === unsaved && prev[which].shown === shown
        ? prev
        : { ...prev, [which]: { unsaved, shown } }
    );
  const canriskChanges = useChangedFields({
    baseline: {},
    values: individualData,
    mode: model?._id && model?.recordType === 'canrisk' ? 'edit' : 'create',
    fields: CANRISK_KEYS
  });
  const resetCanriskBaseline = canriskChanges.resetBaseline;
  const active =
    riskModel === 'canrisk'
      ? {
          unsaved: canriskChanges.unsavedCount,
          shown: canriskChanges.count
        }
      : subCounts[riskModel] || { unsaved: 0, shown: 0 };
  useEffect(() => {
    onChangeCount?.(active.unsaved);
  }, [active.unsaved]); // eslint-disable-line react-hooks/exhaustive-deps
  // Choosing another model puts the open one's inputs away: it asks first.
  const [pendingModel, setPendingModel] = useState(null);
  const chooseModel = (next) => {
    if (next === riskModel) return;
    if (active.unsaved > 0) setPendingModel(next);
    else setRiskModel(next);
  };

  // Score from the loaded record (if calculated), shown in an alert at the top.
  const canriskSummary = (result) => {
    const pr = result?.pedigree_result?.[0];
    const tenYr = pr?.ten_yr_cancer_risk?.[0]?.['breast cancer risk']?.percent;
    const lifetime =
      pr?.lifetime_cancer_risk?.[0]?.['breast cancer risk']?.percent;
    return tenYr != null && lifetime != null
      ? `10-year: ${tenYr}% | Lifetime: ${lifetime}%`
      : null;
  };

  let scoreAlert = null;
  if (
    riskModel === 'gail' &&
    model?.recordType === 'gail' &&
    model?.gailRiskScore != null
  ) {
    scoreAlert = t('GAIL score: {{gailRiskScore}}', {
      gailRiskScore: model.gailRiskScore
    });
  } else if (
    riskModel === 'canrisk' &&
    model?.recordType === 'canrisk' &&
    model?.canriskResult
  ) {
    scoreAlert =
      canriskSummary(model.canriskResult) || t('CanRisk scores calculated');
  } else if (
    riskModel === 'prs' &&
    model?.recordType === 'prs' &&
    model?.prsResult
  ) {
    const { trait, pgsId, zscore, percentile } = model.prsResult;
    const parts = [trait || pgsId].filter(Boolean);
    if (zscore != null) parts.push(`z=${zscore}`);
    if (percentile != null) parts.push(`${percentile}th percentile`);
    scoreAlert = parts.length
      ? t('PRS: {{parts}}', { parts: parts.join(' | ') })
      : null;
  }

  // Load the member's saved individual data (+ proband prefill) when the
  // CanRisk branch is active.
  useEffect(() => {
    if (riskModel !== 'canrisk' || !userId) return;
    axios
      .get(
        `${APICONSTANTS.fillCanRiskModal.replace(
          ':user_id',
          userId
        )}?targetId=${memberId}`
      )
      .then(({ data: resp }) => {
        const page = (resp.pages || []).find(
          (p) => String(p.familyMemberId) === String(memberId)
        );
        const loaded = page?.individualData || {};
        setIndividualData(loaded);
        resetCanriskBaseline(loaded);
      })
      .catch(() => {});
  }, [riskModel, userId, memberId]);

  // Fetch this member's saved peer 'prs' records once, regardless of which
  // tab is active, so a suggestion is ready by the time the clinician looks
  // at the CanRisk tab's PRS fields.
  useEffect(() => {
    if (!userId || !memberId) return;
    axios
      .get(APICONSTANTS.getRiskRecords.replace(':user_id', userId))
      .then(({ data }) => {
        const all = Array.isArray(data) ? data : [];
        const memberPrsRecords = all.filter(
          (r) =>
            r.recordType === 'prs' &&
            String(r.familyMemberId) === String(memberId)
        );
        setPrsRecords(memberPrsRecords);
      })
      .catch(() => {});
  }, [userId, memberId]);

  // Verified CanRisk panel reference (mu/sigma/alpha) — same source of truth
  // the compute-poc route uses, fetched once.
  useEffect(() => {
    axios
      .get(APICONSTANTS.getCanriskPrsReference)
      .then(({ data }) => setCanriskReference(data || {}))
      .catch(() => {});
  }, []);

  // Rebuild suggestions whenever either input changes — decoupled from the
  // two fetches above so neither has to wait on the other to complete first.
  useEffect(() => {
    setPrsSuggestions(buildPrsSuggestions(prsRecords, canriskReference));
  }, [prsRecords, canriskReference]);

  const handleSaveCanrisk = () => {
    if (!userId) {
      return setSnack({
        status: true,
        msg: t('Please save the pedigree first'),
        severity: 'error'
      });
    }
    setSavingCanrisk(true);
    axios
      .put(APICONSTANTS.saveCanRiskIndividualData.replace(':user_id', userId), {
        // Editing a CanRisk tile updates it; otherwise a new record is created.
        recordId: model?.recordType === 'canrisk' ? model?._id : undefined,
        familyMemberId: memberId,
        familyId: user?.familyId,
        individualData
      })
      .then(() => {
        setSnack({
          status: true,
          msg: t('Individual data saved'),
          severity: 'success'
        });
        handleClose({ exit: true });
      })
      .catch(() =>
        setSnack({
          status: true,
          msg: t('Error saving individual data'),
          severity: 'error'
        })
      )
      .finally(() => setSavingCanrisk(false));
  };

  // A calculated result (QRISK3, TGCLOUD-1911; a triage evaluation,
  // TGCLOUD-1910) is read, never edited here. Opened from its tile it used to
  // land on the model chooser with nothing chosen, and Save there wrote an
  // empty CanRisk record.
  if (model?.recordType === 'qrisk3' || model?.recordType === 'triage') {
    return (
      <Box sx={{ p: 1 }}>
        {model.recordType === 'qrisk3' ? (
          <Qrisk3StoredResult result={model.qrisk3Result} />
        ) : (
          <PackResult result={fromStored(model.triageResult)} stored />
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Button
            variant="outlined"
            onClick={() => handleClose({ exit: true })}
          >
            {t('Close')}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1 }}>
      {scoreAlert && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {scoreAlert}
        </Alert>
      )}
      <FormLabel>Risk model</FormLabel>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 0.75 }}>
        {[
          {
            value: 'canrisk',
            label: 'CanRisk',
            description: 'Individual data inputs',
            disabled: isDisabled
          },
          {
            value: 'gail',
            label: 'Gail score',
            description: 'Gail (BCRAT) inputs',
            disabled: isDisabled || !gailAllowed
          },
          {
            value: 'prs',
            label: 'PRS',
            description: 'Polygenic risk score',
            disabled: isDisabled
          },
          ...(measurementsOffered
            ? [
                {
                  value: 'measurements',
                  label: 'Clinical measurements',
                  description: 'Blood pressure, cholesterol, BMI, smoking',
                  disabled: isDisabled
                }
              ]
            : [])
        ].map((opt) => {
          const selected = riskModel === opt.value;
          return (
            <Box
              key={opt.value}
              onClick={() => {
                if (opt.disabled) return;
                chooseModel(opt.value);
              }}
              sx={{
                // A fourth card (clinical measurements, TGCLOUD-1911) wraps
                // rather than squeezing the others.
                flex: '1 1 160px',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1.25,
                border: '1px solid',
                borderColor: selected ? 'secondary.main' : 'divider',
                bgcolor: selected ? 'action.hover' : 'transparent',
                borderRadius: '12px',
                cursor: opt.disabled ? 'not-allowed' : 'pointer',
                opacity: opt.disabled ? 0.5 : 1
              }}
            >
              <Radio size="small" checked={selected} disabled={opt.disabled} />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{ fontWeight: 600, fontSize: 14, lineHeight: 1.2 }}
                >
                  {t(opt.label)}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary'
                  }}
                >
                  {t(opt.description)}
                </Typography>
                {selected && active.shown > 0 && (
                  <ChangedSectionMarker
                    count={active.shown}
                    sx={{ ml: 0, mt: 0.5 }}
                  />
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
      {!gailAllowed && (
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            display: 'block',
            mt: 1
          }}
        >
          {t('Gail score is only available for female individuals.')}
        </Typography>
      )}
      {/* Bar separating the mode selector from the record fields below. */}
      <Divider sx={{ mt: 2, mb: 2 }} />
      <Box sx={formCardSx}>
        {riskModel === 'gail' && (
          // Capture Gail inputs only (no calculation). The Save action lives in
          // the modal footer; the score is produced later from the sidebar
          // "Calculate Gail Score" action.
          <GailForm
            ref={gailRef}
            data={data}
            model={model?.recordType === 'gail' ? model : null}
            hideActionButton
            disabled={isDisabled}
            onSaved={() => handleClose({ exit: true })}
            onChangeCount={reportFrom('gail')}
          />
        )}
        {riskModel === 'canrisk' && (
          <IndividualDataForm
            value={individualData}
            onChange={(next) => setIndividualData(next)}
            disabled={isDisabled}
            prsSuggestions={prsSuggestions}
            changed={canriskChanges.changed}
          />
        )}
        {riskModel === 'measurements' && (
          // A dated set of readings, a record of its own (TGCLOUD-1911).
          <ClinicalMeasurementsForm
            ref={measurementsRef}
            data={data}
            model={model?.recordType === 'measurements' ? model : null}
            disabled={isDisabled}
            onSaved={() => handleClose({ exit: true })}
            onChangeCount={reportFrom('measurements')}
          />
        )}
        {riskModel === 'prs' && (
          // Self-saving, like Gail: no in-app calculation, the score is
          // typed in from a lab report or entered manually.
          <PrsForm
            ref={prsRef}
            data={data}
            model={model?.recordType === 'prs' ? model : null}
            hideActionButton
            disabled={isDisabled}
            onSaved={() => handleClose({ exit: true })}
            onChangeCount={reportFrom('prs')}
          />
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 1,
          mt: 1
        }}
      >
        <UnsavedChangeCount count={active.shown} />
        {/* Asks first while the inputs hold unsaved changes (TGCLOUD-1902). */}
        <Button variant="outlined" onClick={() => handleClose()}>
          {t('Close')}
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => {
            if (riskModel === 'gail') return gailRef.current?.submit();
            if (riskModel === 'prs') return prsRef.current?.submit();
            if (riskModel === 'measurements')
              return measurementsRef.current?.submit();
            return handleSaveCanrisk();
          }}
          disabled={isDisabled || savingCanrisk}
        >
          {t('Save')}
        </Button>
      </Box>
      <UnsavedChangesDialog
        open={pendingModel !== null}
        count={active.unsaved}
        onCancel={() => setPendingModel(null)}
        onDiscard={() => {
          setRiskModel(pendingModel);
          setPendingModel(null);
        }}
      />
    </Box>
  );
}
